import { store, type AppDispatch } from '../../stores/redux';
import { getChatUserIdFromIdentity } from '../../features/realtime/realtime.helpers';
import {
  type RealtimeEvent,
  type RealtimeMessage,
  RealtimeMessageType,
} from '../../features/realtime/realtime.types';
import * as realtime from '../../features/realtime/realtime.slice';
import { setIsTyping, addChatMessage } from '../../features/chat/chat.slice';
import { getMessage } from './helpers/getMessage';
import { getLogger } from '../../libs/logger';
import { ackReceived as ackReceivedAction } from '../../features/realtime/realtime.slice';
import audioNotificationPlayer from '../useAudio/audioNotificationPlayer';
import { NO_SOUND } from '../useAudio';
import { getChatMessageSound } from '../../features/userPreferences/userPreferences.selectors';

const logger = getLogger('initChat');

let isInitialized = false;

type InitChatMessageProps = {
  dispatch: AppDispatch;
  onMessage: typeof realtime.onMessage;
  addChatMessage: typeof addChatMessage;
  messageType: RealtimeMessageType;
};

type InitTypingProps = {
  dispatch: AppDispatch;
  onMessage: typeof realtime.onMessage;
  setIsTyping: typeof setIsTyping;
  messageType: RealtimeMessageType;
};

type AckRequestProps = {
  dispatch: AppDispatch;
  onMessage: typeof realtime.onMessage;
  messageFactory: (event: RealtimeEvent, messageId: string) => RealtimeMessage;
  messageType: RealtimeMessageType;
};

type ReceiveAckProps = {
  dispatch: AppDispatch;
  onMessage: typeof realtime.onMessage;
  ackReceived: typeof ackReceivedAction;
  messageType: RealtimeMessageType;
};

type TypingEventPayload = { isTyping: boolean };

export const listenForChatMessages = (): void => {
  if (isInitialized) {
    return;
  }

  isInitialized = true;
  _listenForChatMessages({
    dispatch: store.dispatch,
    onMessage: realtime.onMessage,
    addChatMessage,
    messageType: RealtimeMessageType.ChatMessage,
  });

  _listenForAckRequest({
    dispatch: store.dispatch,
    onMessage: realtime.onMessage,
    messageFactory: (event, messageId) => ({
      messageType: RealtimeMessageType.MessageAck,
      payload: { messageId },
      messageId,
    }),
    messageType: RealtimeMessageType.ChatMessage,
  });

  _listenForTypingEvent({
    dispatch: store.dispatch,
    onMessage: realtime.onMessage,
    setIsTyping,
    messageType: RealtimeMessageType.ChatIsTyping,
  });

  _listenForAck({
    dispatch: store.dispatch,
    onMessage: realtime.onMessage,
    ackReceived: ackReceivedAction,
    messageType: RealtimeMessageType.MessageAck,
  });
};

export const _listenForTypingEvent = ({
  dispatch,
  onMessage,
  setIsTyping: setIsTypingAction,
  messageType,
}: InitTypingProps): void => {
  void dispatch(
    onMessage({
      messageType,
      callback: (receivedMessage, event): void => {
        const { isTyping } = receivedMessage.payload as TypingEventPayload;
        logger.info('[_listenForTypingEvent] isTyping message received', {
          messageType,
          isTyping,
        });
        const otherUserId = getChatUserIdFromIdentity(event.from);
        dispatch(setIsTypingAction({ isTyping, otherUserId }));
      },
    }),
  );
};

export const _listenForAckRequest = ({
  dispatch,
  onMessage,
  messageFactory,
  messageType,
}: AckRequestProps): void => {
  void dispatch(
    onMessage({
      messageType,
      callback: (payload, event): void => {
        const { requestAck, messageId } = payload;
        if (Boolean(requestAck) && messageId !== undefined) {
          logger.info('[_listenForAckRequest] sending message ACK', {
            messageId,
            from: event.from,
          });
          const message = messageFactory(event, messageId);
          dispatch(
            realtime.sendMessage({
              id: event.from.id,
              message,
            }),
          ).catch((error) => {
            logger.error(
              '[_listenForAckRequest] could not ack chat message',
              error,
            );
          });
        }
      },
    }),
  );
};

export const _listenForAck = ({
  dispatch,
  onMessage,
  ackReceived,
  messageType,
}: ReceiveAckProps): void => {
  void dispatch(
    onMessage({
      messageType,
      callback: (message, event): void => {
        const messageId =
          message.messageId !== undefined
            ? message.messageId
            : (message.payload as Record<string, string> | undefined)?.[
                'messageId'
              ];
        if (messageId !== undefined) {
          logger.info('[_listenForAck] received ACK', {
            messageId,
            from: event.from,
          });
          dispatch(ackReceived({ messageId }));
        }
      },
    }),
  );
};

export const _listenForChatMessages = ({
  dispatch,
  onMessage,
  addChatMessage: addChatMessageAction,
  messageType,
}: InitChatMessageProps): void => {
  void dispatch(
    onMessage({
      messageType,
      callback: (receivedMessage, event): void => {
        logger.info('[_listenForChatMessages] Chat message received', {
          messageType,
        });
        const { content, timestamp, senderId } = getMessage(
          receivedMessage,
          event,
        );
        dispatch(
          addChatMessageAction({
            otherUserId: senderId,
            message: { content, timestamp, senderId, isOwn: false },
          }),
        );

        // Play notification sound
        const audioFile = getChatMessageSound(store.getState());
        if (audioFile !== NO_SOUND) {
          void audioNotificationPlayer.play(audioFile);
        }
      },
    }),
  );
};

export const resetChatInitialization = (): void => {
  isInitialized = false;
};
